import React, { FC, useCallback, useEffect, useState } from 'react';
import MediaLibraryModal from 'app/modules/mediaLibrary/MediaLibraryModal';
import SelectedAttachments from 'app/components/mediaAttachments/components/SelectedAttachments';
import ImageEditor from 'app/components/ImageEditor';
import { AttachmentConvertor } from 'utils/attachments';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import {
  AttachmentCard,
  Attachments,
  VideoMediumAttachmentCard,
} from 'types/Attachments';
import { VideoThumbnail } from 'types/MediaLibrary';
import {
  DEFAULT_FILE_SOURCES,
  FileSourceType,
} from 'app/components/fileSourceInput/hooks/useFileSource';
import { useMediaAttachmentsContainer } from 'app/components/mediaAttachments/hooks/useMediaAttachmentsContainer';
import clsx from 'clsx';
import { MediaLibraryItemFileType } from 'types/MediaLibrary';
import DropZone from './fileSourceInput/dropZone';
import { RunningUpload } from 'types/RunningUpload';
import VideoThumbnailSetupModal from 'app/components/modals/VideoThumbnailSetupModal';
import { Badge, Icon } from '@kontentino/ui';
import Modal from 'components/shared/Modal';
import OptionButton from 'app/modules/textEditor/components/optionsBar/OptionButton';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import AiImageSuggestionsSettings from 'app/modules/aiContent/components/AiImageSuggestionsSettings';
import { useTranslation } from 'react-i18next';
import TextEditorAiContentProvider from 'app/modules/textEditor/components/textEditorAiContent/TextEditorAiContentProvider';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { AI_GENERATE_BUTTON_STYLE_CLASSNAME } from 'app/modules/aiContent/components/AIGenerateButton';
import { useUser } from 'modules/user/userSelector';
import { AIContentPermissions } from 'utils/user/workflowPermissions';

export type MediaAttachmentsProps = Props;

type Props = {
  page: {
    id: number;
    type: number;
  };
  attachments: Attachments;
  onAttachmentsChange: (
    fn: (attachments: Attachments) => Partial<Attachments>,
  ) => void;
  disabled?: boolean;
  maxCount: number;
  allowedFiles: MediaLibraryItemFileType[];
  onRunningUploadsChange?: (runningUploads: RunningUpload[]) => void;
  canSetupVideoThumbnail?: boolean;
  sources?: readonly FileSourceType[];
};

const MediaAttachments: FC<Props> = ({
  attachments,
  disabled,
  onAttachmentsChange,
  page,
  maxCount,
  allowedFiles,
  onRunningUploadsChange,
  canSetupVideoThumbnail,
  sources,
}) => {
  const [videoAttachmentForThumbnail, setVideoAttachmentForThumbnail] =
    useState<VideoMediumAttachmentCard | null>(null);
  const [isAiImageModalOpen, setIsAiImageModalOpen] = useState(false);
  const { t } = useTranslation();
  const user = useUser();
  const container = useMediaAttachmentsContainer({
    attachments,
    maxCount,
    allowedFiles,
    disabled,
    onAttachmentsChange,
    page,
    sources,
  });

  const handleThumbnailSet = useCallback(
    (thumbnail: VideoThumbnail | null) => {
      onAttachmentsChange((prev) => ({
        cards: prev.cards.map((attachment) => ({
          ...attachment,
          medium: {
            ...attachment.medium,
            custom_thumb: thumbnail,
          },
        })) as AttachmentCard[],
      }));
    },
    [onAttachmentsChange],
  );
  const { accountPlanType } = useSubscriptionInfo();

  useEffect(() => {
    onRunningUploadsChange?.(container.mediaUploader.runningUploads);
  }, [container.mediaUploader.runningUploads, onRunningUploadsChange]);

  const inputProps = container.mediaUploader.dropzone.getInputProps();

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div
        data-name="media-attachments"
        className={clsx('tw-flex tw-gap-2', {
          'tw-pointer-events-none tw-cursor-not-allowed tw-opacity-50':
            disabled,
        })}
      >
        <input {...inputProps} data-cy="media-attachments-input" />
        <UserPermissionGate scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}>
          <>
            {container.totalAttachmentsLength === 0 && (
              <DropZone
                {...container.mediaUploader.dropzone.getRootProps()}
                isDragActive={container.mediaUploader.dropzone.isDragActive}
                specifications={{
                  accept: inputProps.accept,
                  multiple: inputProps.multiple,
                }}
                fileSources={container.fileSources}
              >
                <DropZone.DropZoneHelperText />
                <DropZone.DropZoneFileSelect />
                <DropZone.DropZoneActiveDragOverlay />
              </DropZone>
            )}
          </>
        </UserPermissionGate>

        {container.totalAttachmentsLength > 0 && (
          <>
            <SelectedAttachments
              attachmentsCards={container.attachments.cards}
              onRemoveAttachmentAtIndex={container.removeAttachmentAtIndex}
              onCustomThumbnailClick={
                canSetupVideoThumbnail
                  ? setVideoAttachmentForThumbnail
                  : undefined
              }
              onAttachmentIndexMove={container.moveAttachmentIndex}
              updateAttachmentMedium={container.updateAttachmentMedium}
              onEditImage={container.editImage}
              runningUploads={container.mediaUploader.runningUploads}
            />
            {maxCount > container.totalAttachmentsLength && (
              <UserPermissionGate
                scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}
              >
                <DropZone
                  {...container.mediaUploader.dropzone.getRootProps()}
                  isDragActive={container.mediaUploader.dropzone.isDragActive}
                  specifications={{
                    accept: inputProps.accept,
                    multiple: inputProps.multiple,
                  }}
                  fileSources={container.fileSources}
                  removeDefaultClassName
                  className="tw-relative"
                >
                  <DropZone.DropZoneFilePicker />
                  <DropZone.DropZoneActiveDragOverlay />
                </DropZone>
              </UserPermissionGate>
            )}
          </>
        )}
        {(sources ?? DEFAULT_FILE_SOURCES).includes('media-library') && (
          <UserPermissionGate
            scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}
          >
            <MediaLibraryModal
              page={page}
              isShown={container.mediaLibrary.isShown}
              onHide={container.mediaLibrary.hide}
              limits={{
                selectCount: maxCount - attachments.cards.length,
                allowedFiles,
              }}
              onSelectSubmit={(items) =>
                container.addAttachments(
                  AttachmentConvertor.fromMediaLibraryItems(items),
                )
              }
            />
          </UserPermissionGate>
        )}
        {container.imageToEdit && (
          <ImageEditor
            pageId={page.id}
            imageSrc={container.imageToEdit.src}
            onClose={container.hideImageEditor}
            onImageEdited={(file) => {
              if (typeof container.imageToEdit?.index === 'number') {
                container.removeAttachmentAtIndex(container.imageToEdit.index);
              }
              container.mediaUploader.upload({ files: [file] });
            }}
          />
        )}
        {videoAttachmentForThumbnail && (
          <VideoThumbnailSetupModal
            video={videoAttachmentForThumbnail}
            isOpen={!!videoAttachmentForThumbnail}
            onClose={() => setVideoAttachmentForThumbnail(null)}
            onThumbnailSet={handleThumbnailSet}
          />
        )}
      </div>
      {allowedFiles.includes('image') &&
        AIContentPermissions.canCreateVisualContent(user.role) && (
          <TextEditorAiContentProvider
            accountPlanType={accountPlanType}
            disabled={false}
          >
            <div className="tw-flex tw-items-center tw-justify-center">
              <OptionButton
                className={clsx(
                  AI_GENERATE_BUTTON_STYLE_CLASSNAME,
                  'tw-w-auto tw-px-3',
                )}
                variant="primary"
                onClick={() => setIsAiImageModalOpen(true)}
                data-name="ai-image-suggestions_generate-images"
              >
                <Icon icon={faSparkles} />
                <span className="tw-text-sm">
                  {t('aiImageSuggestions.title')}
                </span>
                <Badge className="tw-ml-2">{t('newTag')}</Badge>
              </OptionButton>
            </div>
            <Modal
              open={isAiImageModalOpen}
              onClose={() => setIsAiImageModalOpen(false)}
              size="md"
              isContentHeightAutoResizable={false}
              classNames={{
                overlay: '!tw-bg-grayscale-190/90',
                modal:
                  '!tw-bg-gradient-to-t !tw-from-purple-160/10 !tw-to-grayscale-10 !tw-rounded-2xl',
              }}
            >
              <AiImageSuggestionsSettings
                page={page}
                onSubmitUpload={(files) => {
                  container.mediaUploader.upload({
                    files,
                  });
                  setIsAiImageModalOpen(false);
                }}
              />
            </Modal>
          </TextEditorAiContentProvider>
        )}
    </div>
  );
};

export default MediaAttachments;
